import { SearchOutlined } from '@mui/icons-material';
import { CircularProgress, IconButton } from '@mui/material';
import { Autocomplete, useLoadScript } from '@react-google-maps/api';
import { Libraries } from 'props';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { ICorrespondentResultViewModel } from 'viewModels';
import { useForm } from 'react-hook-form';
import ErrorIcon from '@mui/icons-material/Error';
import { GetAddressByGeocoding } from '../../services/maps';
import { MapContainer } from './styles';
import { Input } from '../Inputs/Input';

type AddressGoogleMaps = {
  number?: number;
  street?: string;
  city?: string;
  uf?: string;
  postalCode?: string;
  geoLocation?: {
    lat?: number;
    lng?: number;
  };
};

const defaultCoord = {
  lat: -23.55052,
  lng: -46.633308,
};

type BaseMapProps = {
  options?: google.maps.MapOptions & {
    zoom?: number;
    changeMarkerWhenPanChange?: boolean;
  };
};

type MapEditableProps = {
  action: 'select-place' | 'find-correspondents';
  onChange?: (place?: google.maps.places.PlaceResult) => void;
  onPlace?: (geoLocation: google.maps.LatLngLiteral | undefined) => void;
};

type MapViewProps = {
  action: 'view-place';
  center: google.maps.LatLngLiteral;
};

type MapProps = BaseMapProps &
  (MapEditableProps | MapViewProps) & {
    errorMessage?: string;
    onChangeAddress?: (address: AddressGoogleMaps) => void;
  };

const MapSearch: React.FC<MapProps> = ({
  action,
  options,
  errorMessage,
  onChangeAddress,
  ...props
}: MapProps) => {
  const coord = useMemo(
    () => (action === 'view-place' ? (props as MapViewProps).center : defaultCoord),
    [action, props],
  );
  const [autoCompleteText, setAutoCompleteText] = useState<string>();
  const [autoComplete, setAutoComplete] = useState<google.maps.places.Autocomplete>();
  const [center, setCenter] = useState<google.maps.LatLngLiteral>(coord);
  const [currentCenter, setCurrentCenter] = useState<google.maps.LatLngLiteral>(coord);
  const [currentPlace, setCurrentPlace] = useState<google.maps.places.PlaceResult>();
  const [marker, setMarker] = useState<google.maps.LatLngLiteral>(coord);
  const [marks] = useState<ICorrespondentResultViewModel[]>([]);
  const [searching] = useState(false);
  const [mapRef, setMapRef] = React.useState<google.maps.Map>();
  const [libraries] = useState<Libraries>(['places']);
  const { isLoaded } = useLoadScript({
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY ?? '',
    libraries,
  });
  const [defaultValues] = useState({
    local: '',
  });

  const { control, setValue } = useForm({
    reValidateMode: 'onChange',
    defaultValues,
  });

  const handleOnLoad = (map: google.maps.Map) => setMapRef(map);

  const handleCenterChanged = async () => {
    if (action === 'view-place') return;
    if (mapRef) {
      const newCenter = mapRef.getCenter();

      if (newCenter) {
        const lat = newCenter.lat();
        const lng = newCenter.lng();
        if (
          !options?.changeMarkerWhenPanChange ||
          (lat === currentCenter.lat && lng === currentCenter.lng)
        )
          return;
        setCurrentCenter({
          lat,
          lng,
        });
        setMarker({
          lat,
          lng,
        });
      }
    }
  };

  /*const getMarks = useCallback(() => {
    SolicitationController.getCorrespondents({
      lat: currentCenter.lat,
      lng: currentCenter.lng,
      radius: 5,
    })
      .then((results) => {
        setMarks((prev) =>
          prev
            .filter((p) => results.some((r) => r.id === p.id))
            .concat(results.filter((r) => !prev.some((p) => p.id === r.id))),
        );
      })
      .finally(() => setSearching(false));
  }, [currentCenter.lat, currentCenter.lng]);*/

  const onLoad = (autocomplete: google.maps.places.Autocomplete) => setAutoComplete(autocomplete);

  const onPlaceChanged = () => {
    if (autoComplete) {
      const place = autoComplete.getPlace();
      const local = place?.geometry?.location;
      if (local) {
        setCenter({
          lat: local.lat(),
          lng: local.lng(),
        });
        setMarker({
          lat: local.lat(),
          lng: local.lng(),
        });
      }
      const number = Number(
        place?.address_components?.find((ac) => ac.types.includes('street_number'))?.short_name,
      );
      const street = place?.address_components?.find((ac) =>
        ac.types.includes('route'),
      )?.short_name;

      const city = place?.address_components?.find((ac) =>
        ac.types.includes('administrative_area_level_2'),
      )?.short_name;

      const uf = place?.address_components?.find((ac) =>
        ac.types.includes('administrative_area_level_1'),
      )?.short_name;

      const postalCode = place?.address_components
        ?.find((ac) => ac.types.includes('postal_code'))
        ?.short_name?.replace(/\D+/g, '');

      const geoLocation = {
        lat: local?.lat(),
        lng: local?.lng(),
      };
      const formatedAddress: AddressGoogleMaps = {
        number,
        street,
        city,
        uf,
        postalCode,
        geoLocation,
      };
      onChangeAddress?.(formatedAddress);
    } else {
      onChangeAddress?.({});
    }
  };

  const handleGetCurrentFormattedAddress = useCallback(async () => {
    const res = await GetAddressByGeocoding(currentCenter);
    const place = res.results?.[0];

    setAutoCompleteText(place?.formatted_address ?? '');

    if (place) {
      setCurrentPlace(place);
      const number = Number(
        place?.address_components?.find((ac) => ac.types.includes('street_number'))?.short_name,
      );
      const street =
        place?.address_components?.find((ac) => ac.types.includes('route'))?.short_name ||
        place?.address_components
          ?.find((ac) => ac.types.includes('establishment'))
          ?.short_name.split('-')[0]
          .trim();

      const city = place?.address_components?.find((ac) =>
        ac.types.includes('administrative_area_level_2'),
      )?.short_name;

      const uf = place?.address_components?.find((ac) =>
        ac.types.includes('administrative_area_level_1'),
      )?.short_name;

      const postalCode = place?.address_components
        ?.find((ac) => ac.types.includes('postal_code'))
        ?.short_name?.replace(/\D+/g, '');

      const geoLocation = {
        lat: currentCenter.lat,
        lng: currentCenter.lng,
      };
      const formatedAddress: AddressGoogleMaps = {
        number,
        street,
        city,
        uf,
        postalCode,
        geoLocation,
      };
      onChangeAddress?.(formatedAddress);
    } else {
      onChangeAddress?.({});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentCenter]);

  useEffect(() => {
    setCurrentCenter(center);
  }, [center]);

  useEffect(() => {
    const timer = setTimeout(() => {
      handleGetCurrentFormattedAddress();
    }, 1000);

    return () => clearTimeout(timer);
  }, [handleGetCurrentFormattedAddress]);

  useEffect(() => {
    (props as MapEditableProps).onChange?.(currentPlace);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPlace]);

  useEffect(() => {
    (props as MapEditableProps).onPlace?.({
      lat: marker.lat,
      lng: marker.lng,
    });
  }, [marker.lat, marker.lng, props]);

  useEffect(() => {
    if (action === 'view-place') return;
    if (navigator.geolocation)
      navigator.geolocation.getCurrentPosition(({ coords }: GeolocationPosition) => {
        setCenter({
          lat: coords.latitude ?? defaultCoord.lat,
          lng: coords.longitude ?? defaultCoord.lng,
        });
        setMarker({
          lat: coords.latitude ?? defaultCoord.lat,
          lng: coords.longitude ?? defaultCoord.lng,
        });
      });
  }, [action]);

  /*useEffect(() => {
    let timer: NodeJS.Timer;
    if (action === 'find-correspondents') {
      setSearching(true);
      timer = setTimeout(() => getMarks(), 1000);
    }
    return () => timer && clearTimeout(timer);
  }, [action, getMarks]);*/

  const handleUserMarkerClick = ({ address }: ICorrespondentResultViewModel) => {
    setCenter({ lat: address.latitude ?? 0, lng: address.longitude ?? 0 });
  };

  useEffect(() => {
    if (autoCompleteText) {
      setValue('local', autoCompleteText);
    }
  }, [autoCompleteText, setValue]);
  return (
    <MapContainer>
      {!isLoaded ? (
        <div className="search-loading">
          <CircularProgress size={15} />
          <p>Carregando mapa...</p>
        </div>
      ) : (
        <>
          {action !== 'view-place' && (
            <div className="search-local">
              <Autocomplete onLoad={onLoad} onPlaceChanged={onPlaceChanged}>
                <Input
                  fullWidth
                  name="local"
                  control={control}
                  type="text"
                  size="small"
                  variant="standard"
                  placeholder="Pesquisar local..."
                  onChange={(e) => {
                    setAutoCompleteText(e.target.value);
                    onPlaceChanged();
                  }}
                  InputProps={{
                    startAdornment: (
                      <IconButton color="primary" type="button" className="iconBtn">
                        <SearchOutlined />
                      </IconButton>
                    ),
                    endAdornment: searching ? (
                      <div className="search-loading">
                        <CircularProgress size={15} />
                        <p>Procurando...</p>
                      </div>
                    ) : null,
                  }}
                />
              </Autocomplete>
              {errorMessage && (
                <span className="fieldError">
                  <ErrorIcon fontSize="inherit" />
                  {errorMessage}
                </span>
              )}
            </div>
          )}
          {/* <GoogleMap
            mapContainerClassName="google-map"
            mapContainerStyle={containerStyle}
            center={center}
            zoom={options?.zoom ?? 15}
            onLoad={handleOnLoad}
            onCenterChanged={handleCenterChanged}
            options={{
              streetViewControl: false,
              fullscreenControl: false,
              mapTypeControl: false,
              clickableIcons: false,
              ...options,
            }}>
            <Marker
              icon={{
                url: IconMarker,
                scaledSize: new google.maps.Size(30, 30),
              }}
              position={marker}
              key={marker.lat + marker.lng}
            />
            {marks.map(({ id, name, address, ...u }) => (
              <InfoBox
                options={{
                  alignBottom: true,
                  pixelOffset: new google.maps.Size(-38, 0, 'px', 'px'),
                }}
                position={
                  { lat: address.latitude, lng: address.longitude } as unknown as google.maps.LatLng
                }>
                <button
                  id="custom-marker"
                  type="button"
                  onClick={() => handleUserMarkerClick({ id, name, address, ...u })}>
                  <div>
                    <img
                      src={
                        `${API_BASE_URL}/file/${id}` ??
                        'https://developers.google.com/maps/documentation/javascript/examples/markerclusterer/m'
                      }
                      alt={name}
                    />
                  </div>
                </button>
              </InfoBox>
            ))}
          </GoogleMap> */}
        </>
      )}
    </MapContainer>
  );
};

export default React.memo(MapSearch);
